import {
  SendToDocumentStepDataName,
  SaveToResultsStepDataName,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
  SINGLE_FILE_INPUT_STEP_DATA_NAME,
  SINGLE_FILE_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {
  getOutputDataReferenceForDataFrame,
  getOutputDataReferenceForScratchFile,
  getOutputStepDataForChart,
} from 'analysis-tool/data/getOutputStepDataForDataFrame';
import {getDocumentSectionLayoutConfig} from 'analysis-tool/outputs/ConfigureDocumentUtil';
import {findScratchPlateOutputReferenceOnStep} from 'analysis-tool/outputs/getScratchPlateOutputReference';
import {getSendToDocumentStepVersion, isSendToDocumentEnabled} from 'analysis-tool/outputs/OutputsUtil';
import {
  AnalysisOutputType,
  ChartOutputConfig,
  DatasetOutputConfig,
  FileOutputConfig,
  ResultOutputConfig,
} from 'analysis-tool/outputs/Types';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {CreateStepParams, PipelineEdgeUpsertById} from 'pipelines/network/PipelineDAO';
import {UNSET_CONFIG_VARIABLE} from 'pipelines/PipelineConfigConstants';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {JSONValue} from 'util/Types';

import {AnalysisTemplateConfigState} from './AnalysisTemplateUtil';

function createExportDataCatalogDatasetSteps(
  templateConfigState: AnalysisTemplateConfigState
): Array<CreateStepParams> {
  return templateConfigState.selectedOutputs
    .filter((output) => output.outputConfig.type === AnalysisOutputType.DATASET)
    .map((output) => {
      const {stepDataName, stepDataIndex: fromStepDataIndex} = getOutputDataReferenceForDataFrame(
        output.outputConfig.step,
        (output.outputConfig as DatasetOutputConfig).dataFrame.id
      )!;
      return {
        stepType: PipelineStepType.EXPORT_DATA_CATALOG_DATASET,
        stepVersion: 0,
        config: {
          folderId: UNSET_CONFIG_VARIABLE,
          datasetName: UNSET_CONFIG_VARIABLE,
        },
        incomingEdges: [
          {
            fromStepId: output.outputConfig.step.id,
            fromStepDataName: stepDataName,
            fromStepDataIndex: fromStepDataIndex ?? 0,
            toStepDataName: SINGLE_DATASET_INPUT_STEP_DATA_NAME,
            toStepDataIndex: 0,
          },
        ],
      };
    });
}

function createExportDataCatalogFileSteps(
  templateConfigState: AnalysisTemplateConfigState
): Array<CreateStepParams> {
  return templateConfigState.selectedOutputs
    .filter((output) => output.outputConfig.type === AnalysisOutputType.FILE)
    .map((output) => {
      const fileOutputConfig = output.outputConfig as FileOutputConfig;
      const reference = getOutputDataReferenceForScratchFile(
        fileOutputConfig.step,
        fileOutputConfig.scratchFile.id
      );
      assert(reference != null, 'Expected file output to reference a scratch file on its source step');
      return {
        stepType: PipelineStepType.EXPORT_DATA_CATALOG_FILE,
        stepVersion: 0,
        config: {
          folderId: UNSET_CONFIG_VARIABLE,
        },
        incomingEdges: [
          {
            fromStepId: fileOutputConfig.step.id,
            fromStepDataName: reference.stepDataName,
            fromStepDataIndex: reference.stepDataIndex,
            toStepDataName: SINGLE_FILE_INPUT_STEP_DATA_NAME,
            toStepDataIndex: 0,
          },
        ],
      };
    });
}

function createSaveToResultsSteps(templateConfigState: AnalysisTemplateConfigState): Array<CreateStepParams> {
  return templateConfigState.selectedOutputs
    .filter((output) => output.outputConfig.type === AnalysisOutputType.RESULT)
    .map((output) => {
      const {newDataMapping, targetSpecificMappingState} = output.outputConfig as ResultOutputConfig;
      const {stepDataName, stepDataIndex: fromStepDataIndex} = getOutputDataReferenceForDataFrame(
        output.outputConfig.step,
        (output.outputConfig as ResultOutputConfig).dataFrame.id
      )!;
      return {
        stepType: PipelineStepType.SAVE_TO_RESULTS,
        stepVersion: 2,
        config: {
          schemaId: newDataMapping.resultSchema!.id,
          columnMapping: newDataMapping.mappings as unknown as JSONValue,
          targetSpecificMapping: targetSpecificMappingState as unknown as JSONValue,
        },
        incomingEdges: [
          {
            fromStepId: output.outputConfig.step.id,
            fromStepDataName: stepDataName,
            fromStepDataIndex: fromStepDataIndex ?? 0,
            toStepDataName: SaveToResultsStepDataName.INPUT_DATASET,
            toStepDataIndex: 0,
          },
        ],
      };
    });
}

function createSendToDocumentStep(
  templateConfigState: AnalysisTemplateConfigState,
  additionalSteps: Array<CreateStepParams>,
  steps: ReadonlyArray<PipelineStep>
): CreateStepParams {
  const incomingEdgesFromExportDataCatalogDatasetSteps = additionalSteps
    .map((step, fromStepIndex) => ({step, fromStepIndex}))
    .filter(({step}) => step.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET)
    .map(({fromStepIndex}, toStepDataIndex) => ({
      fromStepIndex,
      fromStepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
      fromStepDataIndex: 0,
      toStepDataName: SendToDocumentStepDataName.INPUT_DATASETS,
      toStepDataIndex,
    }));

  const incomingEdgesFromExportDataCatalogFileSteps = additionalSteps
    .map((step, fromStepIndex) => ({step, fromStepIndex}))
    .filter(({step}) => step.stepType === PipelineStepType.EXPORT_DATA_CATALOG_FILE)
    .map(({fromStepIndex}, toStepDataIndex) => ({
      fromStepIndex,
      fromStepDataName: SINGLE_FILE_OUTPUT_STEP_DATA_NAME,
      fromStepDataIndex: 0,
      toStepDataName: SendToDocumentStepDataName.INPUT_FILES,
      toStepDataIndex,
    }));

  const incomingEdgesFromSaveToResultsSteps = additionalSteps
    .map((step, fromStepIndex) => ({step, fromStepIndex}))
    .filter(({step}) => step.stepType === PipelineStepType.SAVE_TO_RESULTS)
    .map(({fromStepIndex}, toStepDataIndex) => ({
      fromStepIndex,
      fromStepDataName: SaveToResultsStepDataName.SAVED_RESULTS,
      fromStepDataIndex: 0,
      toStepDataName: SendToDocumentStepDataName.INPUT_SAVED_RESULTS,
      toStepDataIndex,
    }));

  const incomingEdgesFromChartSteps = templateConfigState.selectedOutputs
    .filter((output) => output.outputConfig.type === AnalysisOutputType.CHART)
    .map((output, toStepDataIndex) => {
      const outputStepData = getOutputStepDataForChart(
        output.outputConfig.step,
        (output.outputConfig as ChartOutputConfig).chart.id
      );

      const chartIndexInStep =
        outputStepData?.charts.findIndex(
          (chart) => assertIsAccessible(chart).id === (output.outputConfig as ChartOutputConfig).chart.id
        ) ?? 0;

      return {
        fromStepId: output.outputConfig.step.id,
        fromStepDataName: outputStepData!.name,
        fromStepDataIndex: chartIndexInStep,
        toStepDataName: SendToDocumentStepDataName.INPUT_CHARTS,
        toStepDataIndex,
      } satisfies PipelineEdgeUpsertById;
    });

  const chartOutputs = templateConfigState.selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.CHART
  );
  const datasetOutputs = templateConfigState.selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.DATASET
  );
  const resultOutputs = templateConfigState.selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.RESULT
  );
  const plateOutputs = templateConfigState.selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.PLATE
  );
  const fileOutputs = templateConfigState.selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.FILE
  );

  // One edge per option: a null `fromStepDataIndex` sends the whole source slot, and a null
  // `toStepDataIndex` carries no slot ordinal -- order rides the persisted edge position the backend
  // assigns on write.
  const incomingEdgesFromPlateSteps = plateOutputs.map((output) => {
    assert(output.outputConfig.type === AnalysisOutputType.PLATE, "Output must be of type 'PLATE'");
    const config = output.outputConfig;
    const [firstPlate] = config.scratchPlates;
    assert(firstPlate != null, 'Plate output must have at least one plate');
    const reference = findScratchPlateOutputReferenceOnStep(steps, config.step.id, firstPlate.id);
    return {
      fromStepId: reference.sourceStepId,
      fromStepDataName: reference.stepDataName,
      fromStepDataIndex: null,
      toStepDataName: SendToDocumentStepDataName.INPUT_PLATES,
      toStepDataIndex: null,
    } satisfies PipelineEdgeUpsertById;
  });

  return {
    stepType: PipelineStepType.SEND_TO_DOCUMENT,
    stepVersion: getSendToDocumentStepVersion(),
    config: {
      documentId: UNSET_CONFIG_VARIABLE,
      sectionLayout: getDocumentSectionLayoutConfig(
        chartOutputs,
        datasetOutputs,
        resultOutputs,
        templateConfigState.outputDocumentLayoutConfiguration,
        plateOutputs,
        fileOutputs
      ),
      sectionIndex: UNSET_CONFIG_VARIABLE as unknown as number,
      lockResultTables: templateConfigState.lockResultTables ?? false,
      hideAnalysisChip: false,
    },
    incomingEdges: [
      ...incomingEdgesFromExportDataCatalogDatasetSteps,
      ...incomingEdgesFromExportDataCatalogFileSteps,
      ...incomingEdgesFromSaveToResultsSteps,
      ...incomingEdgesFromChartSteps,
      ...incomingEdgesFromPlateSteps,
    ],
  };
}

/**
 * Convert the `AnalysisTemplateConfigState` into additional steps to add to the new analysis template.
 *
 * Additional steps include any number of `EXPORT_DATA_CATALOG_DATASET`, `EXPORT_DATA_CATALOG_FILE`, or
 * `SAVE_TO_RESULTS` steps, and up to one `SEND_TO_DOCUMENT` step.
 *
 * @package
 */
export default function createAdditionalStepsForNewAnalysisTemplate(
  templateConfigState: AnalysisTemplateConfigState,
  steps: ReadonlyArray<PipelineStep>
): Array<CreateStepParams> {
  if (templateConfigState.selectedOutputs.length === 0) {
    return [];
  }

  const additionalSteps: Array<CreateStepParams> = [
    ...createExportDataCatalogDatasetSteps(templateConfigState),
    ...createExportDataCatalogFileSteps(templateConfigState),
    ...createSaveToResultsSteps(templateConfigState),
  ];

  if (isSendToDocumentEnabled()) {
    additionalSteps.push(createSendToDocumentStep(templateConfigState, additionalSteps, steps));
  }

  return additionalSteps;
}
